import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Users, CalendarDays, Baby, BookOpen, ClipboardCheck, MessageSquare,
  Shield, ArrowLeft, ChevronDown, ChevronUp, Star, Phone, Mail, Clock, CheckCircle2,
  XCircle, AlertCircle, Bell, Copy, Key, LogOut, Plus, Send, ChevronLeft, Camera
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

const statusColors = {
  pending: 'bg-accent/15 text-accent-foreground',
  confirmed: 'bg-success/15 text-success-foreground',
  active: 'bg-success/15 text-success-foreground',
  completed: 'bg-secondary/15 text-secondary-foreground',
  cancelled: 'bg-destructive/15 text-destructive',
  inactive: 'bg-muted text-muted-foreground',
  present: 'bg-success/15 text-success-foreground',
  absent: 'bg-destructive/15 text-destructive',
  late: 'bg-accent/15 text-accent-foreground',
};

const adminTabs = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'tours', label: 'Tours', icon: CalendarDays },
  { id: 'enrollments', label: 'Enroll', icon: Users },
  { id: 'children', label: 'Children', icon: Baby },
  { id: 'progress', label: 'Progress', icon: BookOpen },
  { id: 'attendance', label: 'Attend', icon: ClipboardCheck },
  { id: 'chat', label: 'Chat', icon: MessageSquare },
];

const RatingStars = ({ value, onChange }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange?.(n)}>
        <Star className={`w-4 h-4 ${n <= value ? 'text-accent fill-accent' : 'text-border'}`} />
      </button>
    ))}
  </div>
);

const MobileAdmin = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [pin, setPin] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [tours, setTours] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [children, setChildren] = useState([]);
  const [progress, setProgress] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Chat state
  const [chatThreads, setChatThreads] = useState([]);
  const [activeChatChild, setActiveChatChild] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const chatEndRef = useRef(null);
  const adminPhotoRef = useRef(null);

  // Progress form
  const [progressForm, setProgressForm] = useState({ child_id: '', area: '', rating: 3, notes: '' });
  // Attendance form
  const [attendanceForm, setAttendanceForm] = useState({ child_id: '', date: new Date().toISOString().split('T')[0], status: 'present', notes: '' });
  const [markingAll, setMarkingAll] = useState(false);

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value.slice(-1);
    setPin(newPin);
    setPinError('');
    if (value && index < 3) document.getElementById(`m-pin-${index + 1}`)?.focus();
    if (newPin.every(d => d !== '') && index === 3) loginAdmin(newPin.join(''));
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) document.getElementById(`m-pin-${index - 1}`)?.focus();
  };

  const loginAdmin = async (code) => {
    setPinLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { pin: code });
      localStorage.setItem('admin_token', data.token);
      setAuthenticated(true);
    } catch {
      setPinError('Invalid PIN');
      setPin(['', '', '', '']);
      document.getElementById('m-pin-0')?.focus();
    } finally {
      setPinLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios.get(`${API}/admin/verify`, getAuthHeader())
        .then(() => setAuthenticated(true))
        .catch(() => localStorage.removeItem('admin_token'));
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!authenticated) return;
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [statsRes, toursRes, enrollRes, childrenRes, progressRes, attendRes, msgsRes, chatRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, headers),
        axios.get(`${API}/admin/tour-requests`, headers),
        axios.get(`${API}/admin/enrollments`, headers),
        axios.get(`${API}/admin/children`, headers),
        axios.get(`${API}/admin/progress`, headers),
        axios.get(`${API}/admin/attendance`, headers),
        axios.get(`${API}/admin/communications`, headers),
        axios.get(`${API}/admin/chat`, headers),
      ]);
      setStats(statsRes.data);
      setTours(toursRes.data);
      setEnrollments(enrollRes.data);
      setChildren(childrenRes.data);
      setProgress(progressRes.data);
      setAttendance(attendRes.data);
      setMessages(msgsRes.data);
      setChatThreads(chatRes.data);
    } catch (err) {
      if (err.response?.status === 401) { setAuthenticated(false); localStorage.removeItem('admin_token'); }
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateTourStatus = async (id, status) => {
    try { await axios.put(`${API}/admin/tour-requests/${id}?status=${status}`, {}, getAuthHeader()); toast.success(`Tour ${status}`); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const updateEnrollmentStatus = async (id, status) => {
    try { await axios.put(`${API}/admin/enrollments/${id}?status=${status}`, {}, getAuthHeader()); toast.success(`Enrollment ${status}`); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const addProgress = async () => {
    if (!progressForm.child_id || !progressForm.area) return toast.error('Select child and area');
    try {
      await axios.post(`${API}/admin/progress`, { ...progressForm, date: new Date().toISOString() }, getAuthHeader());
      toast.success('Progress added');
      setProgressForm({ child_id: '', area: '', rating: 3, notes: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const addAttendance = async () => {
    if (!attendanceForm.child_id) return toast.error('Select a child');
    try {
      await axios.post(`${API}/admin/attendance`, attendanceForm, getAuthHeader());
      toast.success('Attendance recorded');
      setAttendanceForm(prev => ({ ...prev, child_id: '', notes: '' }));
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const markAllPresent = async () => {
    if (!children.length) return toast.error('No children enrolled');
    setMarkingAll(true);
    try {
      const date = new Date().toISOString().split('T')[0];
      await Promise.all(children.map(c =>
        axios.post(`${API}/admin/attendance`, { child_id: c.id, date, status: 'present', notes: '' }, getAuthHeader())
      ));
      toast.success(`All ${children.length} children marked present!`);
      fetchData();
    } catch { toast.error('Failed to mark all present'); }
    finally { setMarkingAll(false); }
  };

  const openChat = async (childId) => {
    setActiveChatChild(childId);
    try {
      const { data: msgs } = await axios.get(`${API}/admin/chat/${childId}`, getAuthHeader());
      setChatMessages(msgs);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to load chat'); }
  };

  const sendAdminChat = async () => {
    if (!chatInput.trim() || !activeChatChild) return;
    setSendingChat(true);
    try {
      const { data: msg } = await axios.post(`${API}/admin/chat/${activeChatChild}`, { message: chatInput }, getAuthHeader());
      setChatMessages(prev => [...prev, msg]);
      setChatInput('');
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to send'); }
    finally { setSendingChat(false); }
  };

  const sendAdminPhoto = async (file) => {
    if (!file || !activeChatChild) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data: msg } = await axios.post(`${API}/admin/chat/${activeChatChild}/photo`, formData, getAuthHeader());
      setChatMessages(prev => [...prev, msg]);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { toast.error('Failed to send photo'); }
    finally { setUploadingPhoto(false); }
  };

  const fetchAdminUnread = useCallback(async () => {
    if (!authenticated) return;
    try {
      const { data } = await axios.get(`${API}/admin/chat/unread`, getAuthHeader());
      setAdminUnreadCount(data.unread);
    } catch {}
  }, [authenticated]);

  const markAdminRead = async () => {
    try {
      await axios.post(`${API}/admin/chat/read`, {}, getAuthHeader());
      setAdminUnreadCount(0);
    } catch {}
  };

  // Poll for admin unread count
  useEffect(() => {
    if (!authenticated || activeTab === 'chat') return;
    fetchAdminUnread();
    const interval = setInterval(fetchAdminUnread, 8000);
    return () => clearInterval(interval);
  }, [authenticated, activeTab, fetchAdminUnread]);

  // Poll for new messages in active chat
  useEffect(() => {
    if (activeTab !== 'chat' || !activeChatChild || !authenticated) return;
    const interval = setInterval(async () => {
      try {
        const { data: msgs } = await axios.get(`${API}/admin/chat/${activeChatChild}`, getAuthHeader());
        setChatMessages(msgs);
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTab, activeChatChild, authenticated]);

  const logout = () => { localStorage.removeItem('admin_token'); setAuthenticated(false); setPin(['', '', '', '']); };

  // Login Screen
  if (!authenticated) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] px-6" data-testid="mobile-admin-login">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="bg-white rounded-3xl shadow-elevated border border-border/30 p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                <Shield className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-fredoka font-bold text-xl text-foreground" data-testid="mobile-admin-heading">Admin Access</h2>
              <p className="text-sm text-muted-foreground">Enter your 4-digit PIN</p>
            </div>
            <div className="flex justify-center gap-3">
              {pin.map((digit, i) => (
                <input key={i} id={`m-pin-${i}`} type="password" inputMode="numeric" maxLength={1}
                  value={digit} onChange={(e) => handlePinChange(i, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(i, e)} disabled={pinLoading}
                  className="w-13 h-15 text-center text-2xl font-bold rounded-xl border-2 border-border/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all bg-muted/30"
                  data-testid={`mobile-admin-pin-${i}`}
                />
              ))}
            </div>
            {pinError && <p className="text-center text-destructive text-sm font-medium" data-testid="mobile-admin-error">{pinError}</p>}
            {pinLoading && <p className="text-center text-muted-foreground text-sm">Verifying...</p>}
          </div>
        </motion.div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="pb-4" data-testid="mobile-admin-dashboard">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-center justify-between">
        <h2 className="font-fredoka font-bold text-lg text-foreground">Admin Dashboard</h2>
        <Button variant="ghost" size="sm" onClick={logout} className="text-xs text-muted-foreground" data-testid="mobile-admin-logout">
          <LogOut className="w-4 h-4 mr-1" /> Logout
        </Button>
      </div>

      {/* Tabs - scrollable */}
      <div className="px-4 mb-4 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 w-max">
          {adminTabs.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => { setActiveTab(id); if (id === 'chat') markAdminRead(); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap relative ${activeTab === id ? 'bg-primary text-white shadow-soft' : 'bg-white text-muted-foreground border border-border/40'}`}
              data-testid={`mobile-admin-tab-${id}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
              {id === 'chat' && adminUnreadCount > 0 && activeTab !== 'chat' && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="admin-chat-unread-dot">{adminUnreadCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="px-4">
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>

            {/* Overview */}
            {activeTab === 'overview' && stats && (
              <div className="space-y-3" data-testid="mobile-admin-overview">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Children', value: stats.active_children, color: 'bg-secondary/10 text-secondary' },
                    { label: 'Tours', value: stats.pending_tours, color: 'bg-accent/15 text-accent-foreground' },
                    { label: 'Enrollments', value: stats.total_enrollments, color: 'bg-primary/10 text-primary' },
                    { label: 'Messages', value: stats.total_communications, color: 'bg-success/10 text-success-foreground' },
                  ].map((s, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 border border-border/30 shadow-soft text-center" data-testid={`mobile-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                      <p className="text-2xl font-bold text-foreground">{s.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tours */}
            {activeTab === 'tours' && (
              <div className="space-y-3" data-testid="mobile-admin-tours">
                <h3 className="font-fredoka font-semibold text-foreground">Tour Requests</h3>
                {!tours.length ? (
                  <div className="bg-white rounded-2xl p-6 text-center border border-border/30">
                    <CalendarDays className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No tour requests</p>
                  </div>
                ) : tours.map(t => (
                  <div key={t.id} className="bg-white rounded-2xl p-4 border border-border/30 shadow-soft space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-sm text-foreground">{t.parent_name}</p>
                      <Badge className={`${statusColors[t.status]} text-xs capitalize`}>{t.status}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      <p>{t.preferred_date} at {t.preferred_time}</p>
                      <p>{t.email} | {t.phone}</p>
                    </div>
                    {t.status === 'pending' && (
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" onClick={() => updateTourStatus(t.id, 'confirmed')} className="flex-1 bg-success hover:bg-success/90 text-white rounded-lg text-xs h-8" data-testid={`mobile-tour-confirm-${t.id}`}>Confirm</Button>
                        <Button size="sm" variant="outline" onClick={() => updateTourStatus(t.id, 'cancelled')} className="flex-1 rounded-lg text-xs h-8" data-testid={`mobile-tour-cancel-${t.id}`}>Cancel</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Enrollments */}
            {activeTab === 'enrollments' && (
              <div className="space-y-3" data-testid="mobile-admin-enrollments">
                <h3 className="font-fredoka font-semibold text-foreground">Enrollments</h3>
                {!enrollments.length ? (
                  <div className="bg-white rounded-2xl p-6 text-center border border-border/30">
                    <Users className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No enrollments</p>
                  </div>
                ) : enrollments.map(e => (
                  <div key={e.id} className="bg-white rounded-2xl p-4 border border-border/30 shadow-soft space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-sm text-foreground">{e.parent_name}</p>
                        <p className="text-xs text-muted-foreground">Child: {e.child_name}, Age {e.child_age}</p>
                      </div>
                      <Badge className={`${statusColors[e.status]} text-xs capitalize`}>{e.status}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <p>{e.email} | {e.phone}</p>
                    </div>
                    {e.status === 'pending' && (
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" onClick={() => updateEnrollmentStatus(e.id, 'confirmed')} className="flex-1 bg-success hover:bg-success/90 text-white rounded-lg text-xs h-8" data-testid={`mobile-enroll-confirm-${e.id}`}>Confirm</Button>
                        <Button size="sm" variant="outline" onClick={() => updateEnrollmentStatus(e.id, 'cancelled')} className="flex-1 rounded-lg text-xs h-8">Cancel</Button>
                      </div>
                    )}
                    {e.status === 'confirmed' && (
                      <Button size="sm" onClick={() => updateEnrollmentStatus(e.id, 'active')} className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-lg text-xs h-8" data-testid={`mobile-enroll-activate-${e.id}`}>
                        <Key className="w-3 h-3 mr-1" /> Activate (Send Code)
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Children */}
            {activeTab === 'children' && (
              <div className="space-y-3" data-testid="mobile-admin-children">
                <h3 className="font-fredoka font-semibold text-foreground">Children</h3>
                {!children.length ? (
                  <div className="bg-white rounded-2xl p-6 text-center border border-border/30">
                    <Baby className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No children enrolled</p>
                  </div>
                ) : children.map(c => (
                  <div key={c.id} className="bg-white rounded-2xl p-4 border border-border/30 shadow-soft space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-sm text-foreground">{c.name}</p>
                      <Badge className={`${statusColors[c.status] || 'bg-muted text-muted-foreground'} text-xs capitalize`}>{c.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">Age {c.age} {c.nickname ? `"${c.nickname}"` : ''}</p>
                    <p className="text-xs text-muted-foreground">{c.parent_name} | {c.parent_email}</p>
                    {c.access_code && (
                      <button onClick={() => { navigator.clipboard.writeText(c.access_code); toast.success('Code copied!'); }}
                        className="flex items-center gap-1 text-xs text-secondary font-medium mt-1" data-testid={`mobile-child-code-${c.id}`}>
                        <Copy className="w-3 h-3" /> Code: {c.access_code}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Progress */}
            {activeTab === 'progress' && (
              <div className="space-y-4" data-testid="mobile-admin-progress">
                <h3 className="font-fredoka font-semibold text-foreground">Add Progress Report</h3>
                <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Child</Label>
                    <Select value={progressForm.child_id} onValueChange={v => setProgressForm(p => ({ ...p, child_id: v }))}>
                      <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="mobile-progress-child"><SelectValue placeholder="Select child" /></SelectTrigger>
                      <SelectContent>{children.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Area</Label>
                    <Select value={progressForm.area} onValueChange={v => setProgressForm(p => ({ ...p, area: v }))}>
                      <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="mobile-progress-area"><SelectValue placeholder="Select area" /></SelectTrigger>
                      <SelectContent>
                        {['Reading', 'Writing', 'Math', 'Social Skills', 'Motor Skills', 'Art', 'Language', 'Behavior'].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Rating</Label>
                    <RatingStars value={progressForm.rating} onChange={v => setProgressForm(p => ({ ...p, rating: v }))} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Notes</Label>
                    <Textarea value={progressForm.notes} onChange={e => setProgressForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="rounded-xl text-sm resize-none" placeholder="Progress notes..." data-testid="mobile-progress-notes" />
                  </div>
                  <Button onClick={addProgress} className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-xl" data-testid="mobile-progress-add">
                    <Plus className="w-4 h-4 mr-1" /> Add Report
                  </Button>
                </div>

                {progress.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-muted-foreground">Recent</h4>
                    {progress.slice(0, 10).map((r, i) => (
                      <div key={i} className="bg-white rounded-xl p-3 border border-border/30 text-xs space-y-1">
                        <div className="flex justify-between"><span className="font-semibold">{children.find(c => c.id === r.child_id)?.name || 'Unknown'}</span><Badge variant="outline">{new Date(r.date).toLocaleDateString()}</Badge></div>
                        <p>{r.area} - {'*'.repeat(r.rating)}</p>
                        {r.notes && <p className="text-muted-foreground">{r.notes}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Attendance */}
            {activeTab === 'attendance' && (
              <div className="space-y-4" data-testid="mobile-admin-attendance">
                <h3 className="font-fredoka font-semibold text-foreground">Record Attendance</h3>

                {/* Quick Mark All Present */}
                {children.length > 0 && (
                  <Button
                    onClick={markAllPresent}
                    disabled={markingAll}
                    className="w-full bg-success hover:bg-success/90 text-white rounded-2xl py-5 font-bold shadow-soft text-sm"
                    data-testid="mobile-attend-mark-all"
                  >
                    <CheckCircle2 className="w-5 h-5 mr-2" />
                    {markingAll ? `Marking ${children.length} children...` : `Mark All ${children.length} Children Present`}
                  </Button>
                )}

                <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
                  <p className="text-xs text-muted-foreground font-semibold">Or record individually:</p>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Child</Label>
                    <Select value={attendanceForm.child_id} onValueChange={v => setAttendanceForm(p => ({ ...p, child_id: v }))}>
                      <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="mobile-attend-child"><SelectValue placeholder="Select child" /></SelectTrigger>
                      <SelectContent>{children.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Date</Label>
                      <Input type="date" value={attendanceForm.date} onChange={e => setAttendanceForm(p => ({ ...p, date: e.target.value }))} className="rounded-xl h-10 text-sm" data-testid="mobile-attend-date" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Status</Label>
                      <Select value={attendanceForm.status} onValueChange={v => setAttendanceForm(p => ({ ...p, status: v }))}>
                        <SelectTrigger className="rounded-xl h-10 text-sm" data-testid="mobile-attend-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="present">Present</SelectItem>
                          <SelectItem value="absent">Absent</SelectItem>
                          <SelectItem value="late">Late</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Notes</Label>
                    <Input value={attendanceForm.notes} onChange={e => setAttendanceForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" className="rounded-xl h-10 text-sm" data-testid="mobile-attend-notes" />
                  </div>
                  <Button onClick={addAttendance} className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-xl" data-testid="mobile-attend-add">
                    <Plus className="w-4 h-4 mr-1" /> Record
                  </Button>
                </div>
              </div>
            )}

            {/* Chat */}
            {activeTab === 'chat' && (
              <div data-testid="mobile-admin-chat">
                {!activeChatChild ? (
                  /* Thread List */
                  <div className="space-y-3">
                    <h3 className="font-fredoka font-semibold text-foreground">Parent Chats</h3>
                    {!chatThreads.length ? (
                      <div className="bg-white rounded-2xl p-6 text-center border border-border/30">
                        <MessageSquare className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No conversations yet</p>
                        <p className="text-xs text-muted-foreground mt-1">Parents can start a chat from their portal</p>
                      </div>
                    ) : chatThreads.map(t => (
                      <button
                        key={t.child_id}
                        onClick={() => openChat(t.child_id)}
                        className="w-full bg-white rounded-2xl p-4 border border-border/30 shadow-soft text-left active:scale-[0.98] transition-transform"
                        data-testid={`admin-chat-thread-${t.child_id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-sm text-foreground">{t.child_name}'s Parent</p>
                          <span className="text-[10px] text-muted-foreground">{new Date(t.last_time).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground truncate flex-1">
                            {t.last_sender === 'admin' ? 'You: ' : ''}{t.last_message}
                          </p>
                          <Badge className="bg-secondary/10 text-secondary text-[10px]">{t.count}</Badge>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  /* Chat View */
                  <div className="flex flex-col" style={{ height: 'calc(100vh - 240px)' }}>
                    <div className="flex items-center gap-2 mb-3">
                      <button onClick={() => { setActiveChatChild(null); setChatMessages([]); fetchData(); }} className="p-1.5 rounded-lg hover:bg-muted/50" data-testid="admin-chat-back">
                        <ChevronLeft className="w-5 h-5 text-muted-foreground" />
                      </button>
                      <h3 className="font-fredoka font-semibold text-foreground text-sm">
                        {children.find(c => c.id === activeChatChild)?.name || 'Chat'}
                      </h3>
                    </div>

                    <div className="flex-1 overflow-y-auto space-y-2 pr-1 pb-2 no-scrollbar">
                      {!chatMessages.length ? (
                        <div className="text-center py-8">
                          <p className="text-xs text-muted-foreground">No messages in this conversation</p>
                        </div>
                      ) : chatMessages.map(m => (
                        <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm ${
                            m.sender === 'admin'
                              ? 'bg-primary text-white rounded-br-md'
                              : 'bg-white border border-border/30 text-foreground rounded-bl-md shadow-soft'
                          }`} data-testid={`admin-chat-msg-${m.id}`}>
                            {m.photo_path && (
                              <img src={`${API.replace('/api', '/api/files')}/${m.photo_path}`} alt="Photo" className="w-full max-w-[200px] rounded-xl mb-1.5" loading="lazy" />
                            )}
                            {m.message && <p className="leading-relaxed">{m.message}</p>}
                            <p className={`text-[10px] mt-1 ${m.sender === 'admin' ? 'text-white/60' : 'text-muted-foreground/60'}`}>
                              {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </div>

                    <div className="flex gap-2 pt-3 border-t border-border/20 mt-2">
                      <input
                        type="file" accept="image/*" ref={adminPhotoRef} className="hidden"
                        onChange={(e) => { if (e.target.files?.[0]) sendAdminPhoto(e.target.files[0]); e.target.value = ''; }}
                        data-testid="admin-chat-photo-input"
                      />
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => adminPhotoRef.current?.click()}
                        disabled={uploadingPhoto}
                        className="h-10 w-10 rounded-xl flex-shrink-0 text-muted-foreground hover:text-primary"
                        data-testid="admin-chat-photo-btn"
                      >
                        <Camera className={`w-5 h-5 ${uploadingPhoto ? 'animate-pulse' : ''}`} />
                      </Button>
                      <Input
                        value={chatInput}
                        onChange={e => setChatInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAdminChat(); } }}
                        placeholder="Reply..."
                        className="rounded-xl h-10 text-sm flex-1"
                        disabled={sendingChat}
                        data-testid="admin-chat-input"
                      />
                      <Button
                        onClick={sendAdminChat}
                        disabled={sendingChat || !chatInput.trim()}
                        size="icon"
                        className="bg-primary hover:bg-primary/90 text-white rounded-xl h-10 w-10 flex-shrink-0"
                        data-testid="admin-chat-send"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MobileAdmin;
